import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Art } from '../components/Art';
import { BottomNav, Button, IconTile, InlineNote, Sheet, sheetText, Toggle } from '../components/ui';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { Stage } from '../components/Stage';
import { header_account } from '../art/art';
import { useApp } from '../lib/app';
import { initials, phoneDisplay } from '../lib/format';
import { AddIdentifierSheet } from './AddIdentifier';
import type { Contact, Rewards } from '../lib/types';
import { friendly } from '../lib/errors';
import { LANGS, useLang, type Lang } from '../lib/i18n';
import { rewardValue } from '../lib/rewards';
import { useSoon } from './common';

const VERSION = '1.0';

export function Account() {
  const { api, rider, setRider, signOut } = useApp();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [rewards, setRewards] = useState<Rewards | null>(null);
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [note, , say] = useSoon();
  const [editOpen, setEditOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState<'phone' | 'email' | null>(null);

  useEffect(() => {
    api.contacts().then(setContacts, () => setContacts([]));
    api.rewards().then(setRewards, () => undefined);
  }, [api]);

  const toggleSharing = async (v: boolean) => {
    if (v && contacts && !contacts.length) {
      say(t('acc.addFirst'));
      return nav({ to: '/account/contacts' });
    }
    try {
      setRider(await api.updateMe({ shareTripsAfterDark: v }));
    } catch (e) {
      say(friendly(e, t), 'error');
    }
  };
  const ready = rewards?.available ?? [];

  const places = rider?.places ?? [];
  const row = (first: boolean) => ({ minHeight: first ? 56 : 57, padding: '0 14px 0 16px', borderTop: first ? 0 : '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink)', width: '100%', border: 0, borderTopStyle: 'solid' as const, borderTopWidth: first ? 0 : 1, borderTopColor: 'var(--line)', background: 'transparent', cursor: 'pointer', textAlign: 'left' as const });
  const value = { fontSize: 14, fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap' as const };
  const chev = <Icon name="chevronRight2" size={16} sw={2.2} style={{ color: 'var(--ink3)', flex: 'none' }} />;

  return (
    <Screen name="account" label={t('acc.label')}>
      <div className="zr-scroll">
        <Stage top={0} height={98}>
          <Art piece={header_account} />
        </Stage>
        <div style={{ position: 'relative', margin: 'calc(var(--sat) + 18px) 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 64, height: 64, borderRadius: 32, background: 'var(--pri)', color: 'var(--priInk)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em' }}>
              {initials(rider?.name) || <Icon name="user" size={28} />}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 24, lineHeight: '30px', fontWeight: 700, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rider?.name ?? t('acc.your')}</h1>
              <div style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', fontVariantNumeric: 'tabular-nums' }}>
                {rider ? (rider.phone ? phoneDisplay(rider.phone) : (rider.email ?? '')) : ''} <span style={{ color: 'var(--ink3)' }}>·</span>
                <Icon name="star" size={13} style={{ color: 'var(--acText)', flex: 'none' }} />
                <span className="zr-ellipsis" style={{ minWidth: 0 }}>{rider?.rating != null ? t('acc.rider', { n: rider.rating.toFixed(1) }) : t('acc.newRider')}</span>
              </div>
            </div>
            <button
              type="button"
              aria-label={t('acc.edit')}
              onClick={() => (setName(rider?.name ?? ''), setEditOpen(true))}
              style={{ flex: 'none', width: 44, height: 44, border: 0, borderRadius: 22, background: 'var(--s1)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <Icon name="edit" size={18} />
            </button>
          </div>

          {/* the other way to sign in: shown when the account has it, one tap to add (with a code) when it doesn't */}
          {rider && (
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              {rider.phone && rider.email && (
                <p className="zr-ellipsis" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: 'var(--ink2)', minWidth: 0 }}>
                  <Icon name="mail" size={16} style={{ flex: 'none' }} />
                  {rider.email}
                </p>
              )}
              {!rider.email && (
                <button type="button" className="zr-chip" onClick={() => setAdding('email')}>
                  <Icon name="mail" size={18} />
                  {t('acc.addEmail')}
                </button>
              )}
              {!rider.phone && (
                <button type="button" className="zr-chip" onClick={() => setAdding('phone')}>
                  <Icon name="phone" size={18} />
                  {t('acc.addPhone')}
                </button>
              )}
            </div>
          )}

          <section aria-label={t('acc.safety')} style={{ marginTop: 22, borderRadius: 22, background: 'var(--s1)', overflow: 'hidden' }}>
            <div style={{ height: 56, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <IconTile icon="shield2" size={34} />
              <h2 style={{ margin: 0, fontSize: 17, lineHeight: '22px', fontWeight: 700 }}>{t('acc.safety')}</h2>
            </div>
            <button type="button" className="zr-press-soft" onClick={() => nav({ to: '/account/contacts' })} style={{ ...row(false), minHeight: 61 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, lineHeight: '20px', fontWeight: 700 }}>{t('acc.contacts')}</span>
                <span style={{ display: 'block', fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {contacts == null ? '…' : contacts.length ? contacts.map((c) => c.name).join(', ') : t('acc.addFamily')}
                </span>
              </span>
              {chev}
            </button>
            <div style={{ height: 61, padding: '0 16px', borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, lineHeight: '20px', fontWeight: 700 }}>{t('acc.shareDark')}</span>
                <span style={{ display: 'block', fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>{t('acc.shareDarkSub')}</span>
              </span>
              <Toggle on={!!rider?.shareTripsAfterDark} onChange={(v) => void toggleSharing(v)} label={t('acc.shareDark')} />
            </div>
          </section>
          <InlineNote note={note} style={{ margin: '10px 4px 0' }} />

          <nav aria-label={t('acc.settings')} style={{ marginTop: 12, borderRadius: 22, background: 'var(--s1)', overflow: 'hidden' }}>
            <button type="button" className="zr-press-soft" style={row(true)} onClick={() => nav({ to: '/account/places' })}>
              <Icon name="home" size={20} sw={1.9} style={{ color: 'var(--ink2)', flex: 'none' }} />
              <span style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>{t('acc.places')}</span>
              <span style={value}>{places.length ? places.map((p) => p.name).join(', ') : t('acc.addPlaces')}</span>
              {chev}
            </button>
            <button type="button" className="zr-press-soft" style={row(false)} onClick={() => setPayOpen(true)}>
              <Icon name="cash" size={20} sw={1.9} style={{ color: 'var(--ink2)', flex: 'none' }} />
              <span style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>{t('acc.payment')}</span>
              <span style={{ ...value, color: ready.length ? 'var(--acText)' : value.color }}>{ready.length ? t('acc.payReward', { amount: rewardValue(ready[0]) }) : t('acc.paymentVal')}</span>
              {chev}
            </button>
            <button type="button" className="zr-press-soft" style={row(false)} onClick={() => setLangOpen(true)}>
              <Icon name="globe" size={20} sw={1.9} style={{ color: 'var(--ink2)', flex: 'none' }} />
              <span style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>{t('acc.language')}</span>
              <span style={value}>{t(`lang.${lang}`)}</span>
              {chev}
            </button>
            {/* help / support and "drive with us" need a support channel and the driver app: not shown yet */}
          </nav>


          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              type="button"
              onClick={async () => {
                await signOut();
                nav({ to: '/', replace: true });
              }}
              style={{ height: 44, padding: 0, border: 0, background: 'transparent', color: 'var(--ink2)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              {t('acc.logout')}
            </button>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink3)' }}>{t('acc.version', { v: VERSION })}</span>
          </div>
          <div className="zr-navspace" />
        </div>
      </div>
      <BottomNav active="account" />
      <Sheet open={editOpen} onClose={() => setEditOpen(false)} title={t('acc.nameTitle')}>
        <p style={sheetText}>{t('acc.nameBody')}</p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            try {
              setRider(await api.updateMe({ name: name.trim() }));
              setEditOpen(false);
            } catch (err) {
              say(friendly(err, t), 'error');
            } finally {
              setSaving(false);
            }
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} aria-label={t('acc.nameTitle')} className="zr-field" style={{ marginTop: 16, fontSize: 17, fontWeight: 700 }} />
          <InlineNote note={note} />
          <Button type="submit" busy={saving} disabled={!name.trim()} style={{ marginTop: 16 }}>
            {t('common.save')}
          </Button>
        </form>
      </Sheet>
      <AddIdentifierSheet kind={adding ?? 'email'} open={!!adding} onClose={() => setAdding(null)} />
      <Sheet open={langOpen} onClose={() => setLangOpen(false)} title={t('lang.title')}>
        <div role="radiogroup" aria-label={t('lang.title')} style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {LANGS.map((l: Lang) => {
            const on = l === lang;
            return (
              <button key={l} type="button" role="radio" aria-checked={on} onClick={() => setLang(l)} style={{ height: 56, border: 0, borderRadius: 18, background: on ? 'var(--acSoft)' : 'var(--s1)', boxShadow: on ? 'inset 0 0 0 1.5px var(--acText)' : 'inset 0 0 0 1.5px transparent', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--ink)', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
                {t(`lang.${l}`)}
                {on && <Icon name="check" size={18} sw={2.6} style={{ color: 'var(--acText)' }} />}
              </button>
            );
          })}
        </div>
        {lang === 'hi' && <p style={sheetText}>{t('lang.hiNote')}</p>}
        <Button onClick={() => setLangOpen(false)} style={{ marginTop: 18 }}>
          {t('common.done')}
        </Button>
      </Sheet>
      <Sheet open={payOpen} onClose={() => setPayOpen(false)} title={t('acc.payment')}>
        <div style={{ marginTop: 16, height: 60, borderRadius: 18, background: 'var(--acSoft)', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="cash" size={22} sw={1.9} />
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 16, fontWeight: 700 }}>{t('pay.cash')}</span>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink2)' }}>{t('pay.cashSub')}</span>
          </span>
          <Icon name="check" size={20} sw={2.6} style={{ color: 'var(--acText)' }} />
        </div>
        <InlineNote note={note} />
        <h3 style={{ margin: '22px 0 0', fontSize: 17, fontWeight: 700 }}>{t('rewards.title')}</h3>
        <p style={sheetText}>
          {rewards ? `${t.n('rewards.done', rewards.completedRides)}. ` : ''}
          {t('rewards.body')}
        </p>
        <ul data-testid="milestones" style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(rewards?.milestones ?? []).map((m) => {
            const mine = rewards?.rewards.find((r) => r.milestoneId === m.id);
            const state = mine ? (mine.status === 'AVAILABLE' ? t('rewards.ready') : mine.status === 'APPLIED' ? t('rewards.applied') : t('rewards.used')) : m.reached ? t('rewards.reached') : t.n('rewards.toGo', m.ridesToGo);
            return (
              <li key={m.id} style={{ minHeight: 52, borderRadius: 16, background: 'var(--s1)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <Icon name="star" size={18} style={{ color: mine?.status === 'AVAILABLE' ? 'var(--acText)' : 'var(--ink3)', flex: 'none' }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: '19px', fontWeight: 700 }}>{m.title}</span>
                <span style={{ flex: 'none', fontSize: 13, fontWeight: 700, color: mine?.status === 'AVAILABLE' ? 'var(--acText)' : 'var(--ink2)' }}>{state}</span>
              </li>
            );
          })}
        </ul>
        {rewards?.milestones.some((m) => m.isPlaceholder) && <p style={{ ...sheetText, fontSize: 12.5 }}>{t('rewards.placeholder')}</p>}
        <Button variant="secondary" onClick={() => setPayOpen(false)} style={{ marginTop: 16 }}>
          {t('common.close')}
        </Button>
      </Sheet>
    </Screen>
  );
}
